<template>
  <div v-if="isVisible" class="edit-overlay">
    <div class="edit-overlay-backdrop" @click.self="close"></div>
    <div class="edit-overlay-content">
      <div class="edit-header">
        <h2>Edit Metering Point</h2>
        <button @click="close" class="close-button" :disabled="loading">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="form-container">
        <div v-if="loading" class="loading-overlay">
          <div class="loading-spinner"></div>
          <span>{{ loadingMessage }}</span>
        </div>

        <div v-else class="form-content">
          <form @submit.prevent="updateMeteringPoint">
            <!-- MPID (Read-only) -->
            <div class="form-group">
              <label for="mpid">MPID</label>
              <input
                  id="mpid"
                  type="text"
                  :value="formData.mpid"
                  readonly
                  class="form-input readonly-input"
              />
            </div>

            <!-- Street -->
            <div class="form-group">
              <label for="street">Street <span class="required">*</span></label>
              <input
                  id="street"
                  type="text"
                  v-model="formData.street"
                  class="form-input"
                  :class="{ 'error': errors.street }"
                  @blur="onAddressFieldChange"
                  required
              />
              <span v-if="errors.street" class="error-message">{{ errors.street }}</span>
            </div>

            <!-- City -->
            <div class="form-group">
              <label for="city">City <span class="required">*</span></label>
              <input
                  id="city"
                  type="text"
                  v-model="formData.city"
                  class="form-input"
                  :class="{ 'error': errors.city }"
                  @blur="onAddressFieldChange"
                  required
              />
              <span v-if="errors.city" class="error-message">{{ errors.city }}</span>
            </div>

            <!-- Customer Name -->
            <div class="form-group">
              <label for="customer_fname">Customer Name <span class="required">*</span></label>
              <input
                  id="customer_fname"
                  type="text"
                  v-model="formData.customer_fname"
                  class="form-input"
                  :class="{ 'error': errors.customer_fname }"
                  required
              />
              <span v-if="errors.customer_fname" class="error-message">{{ errors.customer_fname }}</span>
            </div>

            <!-- Zip Code -->
            <div class="form-group">
              <label for="zip_code">Zip Code</label>
              <input
                  id="zip_code"
                  type="text"
                  v-model="formData.zip_code"
                  class="form-input"
                  @blur="onAddressFieldChange"
              />
            </div>

            <!-- Country -->
            <div class="form-group">
              <label for="country">Country</label>
              <input
                  id="country"
                  type="text"
                  v-model="formData.country"
                  class="form-input"
                  @blur="onAddressFieldChange"
              />
            </div>

            <!-- Auto-Geocoding Toggle -->
            <div class="form-group">
              <div class="geocoding-toggle-container">
                <div class="toggle-wrapper">
                  <span class="toggle-text">Auto-geocoding</span>
                  <label class="toggle-switch">
                    <input
                        type="checkbox"
                        v-model="autoGeocoding"
                        @change="onAutoGeocodingToggle"
                        class="toggle-checkbox"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                  <div v-if="geocodingInProgress" class="loading-indicator">
                    <svg class="loading-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                    <span>Geocoding...</span>
                  </div>
                </div>
                <div class="geocoding-info">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{{ autoGeocoding ? 'Coordinates will be calculated automatically from address' : 'Enter coordinates manually or enable auto-geocoding' }}</span>
                </div>
              </div>
            </div>

            <!-- Geocoding Status Messages -->
            <div v-if="geocodingMessage" class="geocoding-message" :class="geocodingMessageType">
              <svg v-if="geocodingMessageType === 'success'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20,6 9,17 4,12"></polyline>
              </svg>
              <svg v-else-if="geocodingMessageType === 'error'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              <span>{{ geocodingMessage }}</span>
            </div>

            <!-- Coordinates Row -->
            <div class="coordinates-row">
              <div class="form-group">
                <label for="lat">Latitude</label>
                <input
                    id="lat"
                    type="number"
                    step="any"
                    v-model.number="formData.lat"
                    class="form-input"
                    :class="{ 'geocoded': wasGeocoded, 'disabled': autoGeocoding }"
                    :disabled="autoGeocoding"
                    placeholder="e.g. 45.4654"
                />
                <span class="field-description">
                  {{ autoGeocoding ? 'Auto-calculated from address' : 'Enter manually or enable auto-geocoding' }}
                </span>
              </div>
              <div class="form-group">
                <label for="lon">Longitude</label>
                <input
                    id="lon"
                    type="number"
                    step="any"
                    v-model.number="formData.lon"
                    class="form-input"
                    :class="{ 'geocoded': wasGeocoded, 'disabled': autoGeocoding }"
                    :disabled="autoGeocoding"
                    placeholder="e.g. 9.1859"
                />
                <span class="field-description">
                  {{ autoGeocoding ? 'Auto-calculated from address' : 'Enter manually or enable auto-geocoding' }}
                </span>
              </div>
            </div>

            <!-- Error message -->
            <div v-if="generalError" class="attach-error-message">
              {{ generalError }}
            </div>
          </form>
        </div>
      </div>

      <div class="edit-actions">
        <button
            type="button"
            class="action-button delete-button"
            @click="onDeleteMpid"
            :disabled="loading || isDeleting"
        >
          {{ isDeleting ? 'Deleting...' : 'Delete' }}
        </button>
        <button type="button" @click="close" class="action-button" :disabled="loading">
          Cancel
        </button>
        <button
            type="submit"
            @click="updateMeteringPoint"
            class="action-button submit-button"
            :disabled="loading || !isFormValid"
        >
          <span v-if="loading" class="loading-spinner"></span>
          {{ loading ? 'Updating...' : 'Update Metering Point' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import MpidApiService from '@/services/MpidApiService';

export default {
  name: 'MpidDetailsInfoEditOverlay',
  props: {
    isVisible: { type: Boolean, default: false },
    meteringPoint: { type: Object, required: true }
  },
  data() {
    return {
      loading: false,
      loadingMessage: 'Updating metering point...',
      autoGeocoding: false, // Toggle state for automatic geocoding
      geocodingInProgress: false,
      geocodingDebounceTimer: null,
      generalError: '',
      errors: {},
      isDeleting: false,
      geocodingMessage: '',
      geocodingMessageType: 'info', // 'info', 'success', 'error'
      wasGeocoded: false,
      formData: {
        mpid: '',
        street: '',
        city: '',
        customer_fname: '',
        zip_code: '',
        country: '',
        lat: null,
        lon: null
      }
    };
  },
  computed: {
    isFormValid() {
      return this.formData.street.trim() !== '' &&
          this.formData.city.trim() !== '' &&
          this.formData.customer_fname.trim() !== '';
    },
    canGeocode() {
      return this.formData.street.trim() !== '' && this.formData.city.trim() !== '';
    }
  },
  watch: {
    meteringPoint: {
      immediate: true,
      handler(mp) {
        if (mp) {
          this.initializeForm();
        }
      }
    },
    isVisible(newVal) {
      if (newVal && this.meteringPoint) {
        this.initializeForm();
        this.clearGeocodingMessage();
      }
    }
  },
  methods: {
    initializeForm() {
      const mp = this.meteringPoint;
      this.formData = {
        mpid: mp.mpid || '',
        street: mp.street || '',
        city: mp.city || '',
        customer_fname: mp.customer_fname || mp.customerfname || '',
        zip_code: mp.zip_code || mp.zipcode || '',
        country: mp.country || '',
        lat: mp.lat ? parseFloat(mp.lat) : null,
        lon: mp.lon ? parseFloat(mp.lon) : null
      };

      // Enable auto-geocoding if no coordinates exist but address exists
      this.autoGeocoding = (!this.formData.lat && !this.formData.lon) &&
          (this.formData.street && this.formData.city);

      this.errors = {};
      this.generalError = '';
      this.wasGeocoded = false;
      this.clearGeocodingMessage();
    },

    validateForm() {
      this.errors = {};

      if (!this.formData.street.trim()) {
        this.errors.street = 'Street is required';
      }

      if (!this.formData.city.trim()) {
        this.errors.city = 'City is required';
      }

      if (!this.formData.customer_fname.trim()) {
        this.errors.customer_fname = 'Customer name is required';
      }

      return Object.keys(this.errors).length === 0;
    },

    onAutoGeocodingToggle() {
      if (this.autoGeocoding) {
        // When enabling auto-geocoding, trigger geocoding if address is available
        if (this.canGeocode) {
          // Debounce to avoid instant coordinate change on toggle
          clearTimeout(this.geocodingDebounceTimer);
          this.geocodingDebounceTimer = setTimeout(() => {
            this.performAutoGeocoding();
          }, 1000); // wait 1s after toggle
        }
      } else {
        // When disabling auto-geocoding, clear any geocoding messages
        this.clearGeocodingMessage();
        this.wasGeocoded = false;
      }
    },

    onAddressFieldChange() {
      // Clear geocoding message when address fields change
      if (this.geocodingMessage && this.geocodingMessageType === 'success') {
        this.clearGeocodingMessage();
        this.wasGeocoded = false;
      }

      // If auto-geocoding is enabled, automatically geocode when address changes
      if (this.autoGeocoding && this.canGeocode) {
        // Debounce the geocoding to avoid too many requests
        clearTimeout(this.geocodingDebounceTimer);
        this.geocodingDebounceTimer = setTimeout(() => {
          this.performAutoGeocoding();
        }, 1000); // Wait 1 second after user stops typing
      }
    },

    clearGeocodingMessage() {
      this.geocodingMessage = '';
      this.geocodingMessageType = 'info';
    },

    async performAutoGeocoding() {
      if (!this.canGeocode || this.geocodingInProgress) {
        return;
      }

      this.geocodingInProgress = true;
      this.clearGeocodingMessage();

      try {
        const addressData = {
          street: this.formData.street.trim(),
          city: this.formData.city.trim(),
          zip_code: this.formData.zip_code?.trim() || null,
          country: this.formData.country?.trim() || null
        };

        // Remove null/empty values
        Object.keys(addressData).forEach(key => {
          if (addressData[key] === null || addressData[key] === '') {
            delete addressData[key];
          }
        });


        const response = await MpidApiService.geocodeAddress(addressData);

        // Handle the new backend response format
        if (response && response.lat !== undefined && response.lon !== undefined) {
          this.formData.lat = parseFloat(response.lat);
          this.formData.lon = parseFloat(response.lon);
          this.wasGeocoded = true;

          // Create a concise success message
          let message = `Coordinates: ${response.lat.toFixed(6)}, ${response.lon.toFixed(6)}`;

          if (response.parsed_address?.street_name) {
            message += ` (${response.parsed_address.street_name})`;
          }

          this.showGeocodingMessage(message, 'success');
        } else {
          this.showGeocodingMessage('Auto-geocoding failed: No coordinates found', 'error');
        }

      } catch (error) {
        console.error('Error auto-geocoding address:', error);
        let errorMessage = 'Auto-geocoding failed. Disable auto-geocoding to enter coordinates manually.';

        // Handle specific error messages from the backend
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        }

        this.showGeocodingMessage(errorMessage, 'error');
      } finally {
        this.geocodingInProgress = false;
      }
    },

    showGeocodingMessage(message, type = 'info') {
      this.geocodingMessage = message;
      this.geocodingMessageType = type;

      // Auto-clear success messages after 8 seconds (longer for longer messages)
      if (type === 'success') {
        setTimeout(() => {
          if (this.geocodingMessageType === 'success') {
            this.clearGeocodingMessage();
          }
        }, 8000);
      }
    },
    async onDeleteMpid() {
      const mpid = this.formData?.mpid || this.meteringPoint?.mpid; // ensure we have an mpid
      if (!mpid) {
        console.warn('No MPID found for deletion.');
        return;
      }
      if (!confirm('Delete this metering point?')) return;

      this.isDeleting = true;
      try {
        await MpidApiService.deleteMeteringPoint(mpid);
        this.$emit('deleted', mpid);  // let parent refresh/navigate
        // navigate to Metering Points page
        this.$router.push({ path: '/meteringpoints' });
      } catch (err) {
        console.error('Delete MPID failed:', err);
        alert('Failed to delete metering point. Metering point is attached to active device.');
      } finally {
        this.isDeleting = false;
      }
    },


    async updateMeteringPoint() {
      if (!this.validateForm()) {
        return;
      }

      this.loading = true;
      this.loadingMessage = 'Updating metering point...';
      this.generalError = '';

      try {
        // Prepare data for backend
        const updateData = {
          street: this.formData.street.trim(),
          city: this.formData.city.trim(),
          customer_fname: this.formData.customer_fname.trim(),
          zip_code: this.formData.zip_code?.trim() || null,
          country: this.formData.country?.trim() || null,
          lat: this.formData.lat,
          lon: this.formData.lon
        };

        // Remove null/empty values for optional fields, but keep lat/lon even if null
        Object.keys(updateData).forEach(key => {
          if (key !== 'lat' && key !== 'lon' && (updateData[key] === null || updateData[key] === '')) {
            delete updateData[key];
          }
        });

        const updatedMeteringPoint = await MpidApiService.updateMeteringPoint(this.formData.mpid, updateData);

        this.$emit('updated', updatedMeteringPoint);
        this.close();
      } catch (error) {
        console.error('Error updating metering point:', error);
        this.generalError = error.response?.data?.message || 'Failed to update metering point. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    close() {
      if (!this.loading) {
        // Clear any pending geocoding timers
        if (this.geocodingDebounceTimer) {
          clearTimeout(this.geocodingDebounceTimer);
        }
        this.clearGeocodingMessage();
        this.wasGeocoded = false;
        this.$emit('close');
      }
    }
  },

  beforeUnmount() {
    if (this.geocodingDebounceTimer) {
      clearTimeout(this.geocodingDebounceTimer);
    }
  }
};
</script>

<style scoped>
/* Overlay */
.edit-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.edit-overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(32,32,32,0.1) 0%, rgba(16,16,16,0.95) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease forwards;
}

.edit-overlay-content {
  position: relative;
  width: 700px;
  max-width: 90%;
  max-height: 90vh;
  border-radius: 8px;
  padding: 24px;
  color: white;
  background-color: rgba(50,50,50,0.2);
  -webkit-backdrop-filter: blur(16px);
  backdrop-filter: blur(16px);
  overflow-y: auto;
  animation: slideIn 0.3s ease forwards;
  z-index: 1001;
}

/* Header */
.edit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  padding-bottom: 16px;
}

.edit-header h2 {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
}

.close-button {
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.6);
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.close-button:hover:not(:disabled) {
  color: rgba(255,255,255,0.9);
}

.close-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* Form container */
.form-container {
  position: relative;
  min-height: 200px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  background-color: rgba(0,0,0,0.6);
  border-radius: 8px;
  gap: 16px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255,255,255,0.1);
  border-top: 3px solid rgba(255,255,255,0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.form-content {
  width: 100%;
}

.form-group {
  margin-bottom: 18px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.required {
  color: #ff8a80;
  font-weight: 600;
}

.form-input {
  width: 100%;
  background-color: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 4px;
  color: white;
  padding: 10px 12px;
  font-size: 13px;
  transition: border-color 0.2s, background-color 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: rgba(79,195,247,0.5);
  background-color: rgba(79,195,247,0.05);
}

.form-input.error {
  border-color: #ff8a80;
}

.readonly-input {
  background-color: rgba(255, 255, 255, 0.02);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}

.coordinates-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.error-message {
  color: #ff8a80;
  font-size: 11px;
  margin-top: 4px;
  display: block;
  font-weight: 500;
}

.field-description {
  color: rgba(255, 255, 255, 0.5);
  font-size: 10px;
  margin-top: 4px;
  display: block;
  font-style: italic;
}

.info-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.3);
  border-radius: 4px;
  color: rgba(33, 150, 243, 0.9);
  font-size: 12px;
}

.info-message svg {
  flex-shrink: 0;
}

.attach-error-message {
  color: #ff8a80;
  text-align: center;
  margin-top: 16px;
  font-size: 13px;
}

/* Action buttons */
.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255,255,255,0.1);
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover:not(:disabled) {
  background-color: rgba(255,255,255,0.1);
}

.action-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.submit-button {
  background-color: rgba(79,195,247,0.2);
}

.submit-button:hover:not(:disabled) {
  background-color: rgba(79,195,247,0.3);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive design */
@media (max-width: 576px) {
  .edit-overlay-content {
    padding: 16px;
  }

  .coordinates-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .edit-actions {
    flex-direction: column-reverse;
    gap: 8px;
  }

  .action-button {
    width: 100%;
    justify-content: center;
  }
}
form-input.disabled {
  background-color: rgba(255, 255, 255, 0.02);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}

.geocoded {
  border-color: rgba(76, 175, 80, 0.5) !important;
  background-color: rgba(76, 175, 80, 0.05) !important;
}

.geocoding-toggle-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
}

.toggle-text {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  cursor: pointer;
  user-select: none;
}

.toggle-checkbox {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.toggle-slider {
  position: relative;
  display: block;
  width: 44px;
  height: 24px;
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 24px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.toggle-slider::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-checkbox:checked + .toggle-slider {
  background-color: rgba(79, 195, 247, 0.4);
  border-color: rgba(79, 195, 247, 0.6);
}

.toggle-checkbox:checked + .toggle-slider::before {
  transform: translateX(20px);
  background-color: #4fc3f7;
  box-shadow: 0 2px 6px rgba(79, 195, 247, 0.3);
}

.toggle-switch:hover .toggle-slider {
  background-color: rgba(255, 255, 255, 0.2);
}

.toggle-checkbox:checked + .toggle-slider:hover {
  background-color: rgba(79, 195, 247, 0.5);
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: rgba(79, 195, 247, 0.8);
  font-weight: 500;
  margin-left: auto;
}

.loading-icon {
  animation: spin 1s linear infinite;
}

.geocoding-info {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  line-height: 1.5;
}

.geocoding-info svg {
  flex-shrink: 0;
  margin-top: 2px;
  opacity: 0.6;
}

.geocoding-info span {
  font-style: italic;
}

.delete-button {
  color: #ff8a80;
  border: 1px solid rgba(255, 138, 128, 0.4);
}

.delete-button:hover:not(:disabled) {
  background-color: rgba(255, 138, 128, 0.12);
}
</style>